// MessagesPage — Direct messages inbox
// Layout: conversation list, last message, unread badge, online status

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';

interface Conversation {
  id: string;
  user: string;
  avatar: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  lastMessage: string;
  time: string;
  unread: number;
  online: boolean;
  pinned?: boolean;
  typing?: boolean;
}

const CONVOS: Conversation[] = [
  { id: 'c1', user: 'Marcus Chen',   avatar: 'user-1', tier: 'Professional', lastMessage: 'NQ trade closed. +$420. Want to copy my next setup?', time: '2m',  unread: 2, online: true,  pinned: true },
  { id: 'c2', user: 'Sofia Petrov',  avatar: 'user-2', tier: 'Professional', lastMessage: 'typing...',                                                        time: '5m',  unread: 0, online: true,  typing: true },
  { id: 'c3', user: 'Funder Pro Support', avatar: 'support', tier: 'Beginner', lastMessage: 'Your KYC is approved. You can now activate a challenge.',       time: '1h',  unread: 1, online: true },
  { id: 'c4', user: 'Aiden Wright',  avatar: 'user-3', tier: 'Professional', lastMessage: 'Great meeting you at the webinar today!',                       time: '3h',  unread: 0, online: false },
  { id: 'c5', user: 'Lena Voss',     avatar: 'user-4', tier: 'Mid',          lastMessage: 'I just published a new bot. Would love your feedback!',         time: '1d',  unread: 0, online: false },
  { id: 'c6', user: 'Yuki Tanaka',   avatar: 'user-4', tier: 'Mid',          lastMessage: 'Thanks for the follow! 🚀',                                     time: '2d',  unread: 0, online: false },
  { id: 'c7', user: 'Diego Rivera',  avatar: 'user-3', tier: 'Professional', lastMessage: 'Let\'s set up a copy-trade. My breakout strategy is open.',     time: '4d',  unread: 0, online: false },
];

export function MessagesPage() {
  const nav = useNavigate();
  const [convos, setConvos] = useState(CONVOS);
  const total = convos.reduce((s, c) => s + c.unread, 0);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Messages {total > 0 && <span className="ml-1 text-[11px] text-tf-loss">{total}</span>}</div>
        <button onClick={() => alert('New message')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" /></svg>
          <input type="text" placeholder="Search messages" data-testid="msg-search" className="h-9 w-full rounded-[10px] bg-white border border-tf-border pl-9 pr-3 text-[14px] text-tf-text-1 focus:outline-none focus:ring-2 focus:ring-tf-blue/30 placeholder:text-tf-text-3" />
        </div>
      </div>

      {/* Conversations */}
      <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {convos.map((c, i) => (
          <button
            key={c.id}
            onClick={() => nav('/portal/messages/' + c.id)}
            data-testid={`convo-${c.id}`}
            className={['flex w-full items-start gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < convos.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <div className="relative">
              <UserAvatar size={48} name={c.user} />
              {c.online && <div className="absolute right-0 bottom-0 h-3 w-3 rounded-full bg-tf-profit border-2 border-white" />}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[14px] font-semibold text-tf-text-1 truncate">{c.user}</span>
                  {c.pinned && <span className="text-[10px] text-tf-text-3">📌</span>}
                </div>
                <span className={['shrink-0 text-[11px]', c.unread > 0 ? 'text-tf-blue font-semibold' : 'text-tf-text-3'].join(' ')}>{c.time}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                {c.tier !== 'Beginner' && <TierPill tier={c.tier} />}
              </div>
              <div className={['mt-0.5 text-[13px] truncate', c.unread > 0 ? 'text-tf-text-1 font-semibold' : 'text-tf-text-3'].join(' ')}>
                {c.typing ? <span className="text-tf-blue italic">typing...</span> : c.lastMessage}
              </div>
            </div>
            {c.unread > 0 && (
              <div className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-tf-loss px-1.5 text-[10px] font-bold text-white">
                {c.unread}
              </div>
            )}
          </button>
        ))}
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">Messages are end-to-end encrypted.</p>
    </div>
  );
}
